import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Loader2, AlertCircle, RefreshCw } from 'lucide-react'
import { SnapshotList } from '@/components/attendance-metrics/SnapshotList'
import { useSnapshots, useDeleteSnapshot } from '@/hooks/useAttendanceMetrics'
import { ROUTES } from '@/lib/constants'
import { toast } from 'sonner'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'

export function SnapshotsListPage() {
  const navigate = useNavigate()
  const [deleteId, setDeleteId] = useState<string | null>(null)
  const [selectedIds, setSelectedIds] = useState<string[]>([])

  const snapshotsQuery = useSnapshots()
  const deleteMutation = useDeleteSnapshot()

  const handleDelete = async () => {
    if (!deleteId) return

    try {
      await deleteMutation.mutateAsync(deleteId)
      toast.success('Relatório deletado com sucesso!')
      setDeleteId(null)
    } catch (error) {
      toast.error('Erro ao deletar relatório')
    }
  }

  const handleCompare = () => {
    if (selectedIds.length !== 2) return
    // Navigate to comparison view (could be implemented later)
    toast.info('Funcionalidade de comparação será implementada em breve')
  }

  const handleSnapshotClick = (id: string) => {
    navigate(ROUTES.ATTENDANCE_METRICS_SNAPSHOT_DETAIL(id))
  }

  // Loading state
  if (snapshotsQuery.isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-2">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-muted-foreground">Carregando relatórios...</p>
        </div>
      </div>
    )
  }

  // Error state
  if (snapshotsQuery.error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-4 max-w-md text-center">
          <AlertCircle className="h-12 w-12 text-destructive" />
          <div>
            <h3 className="text-lg font-semibold mb-2">Erro ao carregar relatórios</h3>
            <p className="text-sm text-muted-foreground">
              {snapshotsQuery.error instanceof Error
                ? snapshotsQuery.error.message
                : 'Ocorreu um erro inesperado'}
            </p>
          </div>
          <Button onClick={() => snapshotsQuery.refetch()}>Tentar novamente</Button>
        </div>
      </div>
    )
  }

  const snapshots = snapshotsQuery.data || []

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">Relatórios Salvos</h1>
          <p className="text-muted-foreground">
            Relatórios consolidados de métricas de atendimento
          </p>
        </div>
        <Button
          variant="outline"
          size="icon"
          onClick={() => snapshotsQuery.refetch()}
          disabled={snapshotsQuery.isFetching}
          title="Atualizar lista"
        >
          {snapshotsQuery.isFetching ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4" />
          )}
        </Button>
      </div>

      {/* Snapshots List */}
      <SnapshotList
        snapshots={snapshots}
        selectedIds={selectedIds}
        onSelectionChange={setSelectedIds}
        onCompare={handleCompare}
        onDelete={(id) => setDeleteId(id)}
        onSnapshotClick={handleSnapshotClick}
        isLoading={snapshotsQuery.isLoading}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja deletar este relatório? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Deletar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
